const SKIP_UNDER_BYTES = 300 * 1024;
const JPEG_QUALITY = 0.82;

/** Upload presets. JPEG outputs use quality 0.82. Partner logos stay PNG. */
export const IMAGE_COMPRESS_PRESETS = {
  hero: { maxWidth: 1600, format: 'jpeg' },
  headshot: { maxWidth: 600, format: 'jpeg' },
  'speaker-photo': { maxWidth: 600, format: 'jpeg' },
  'partner-logo': { maxWidth: 400, format: 'png' },
};

function isGifOrSvg(file) {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  if (type === 'image/gif' || type === 'image/svg+xml') return true;
  return name.endsWith('.gif') || name.endsWith('.svg');
}

function replaceExtension(name, ext) {
  const raw = String(name || '').trim();
  const slash = Math.max(raw.lastIndexOf('/'), raw.lastIndexOf('\\'));
  const base = slash >= 0 ? raw.slice(slash + 1) : raw;
  const dot = base.lastIndexOf('.');
  const stem = dot > 0 ? base.slice(0, dot) : base.replace(/^\.+/, '');
  return `${stem || 'image'}.${ext}`;
}

function outputSpec(format) {
  if (format === 'png') return { mime: 'image/png', ext: 'png' };
  return { mime: 'image/jpeg', ext: 'jpg', quality: JPEG_QUALITY };
}

async function createBitmap(file) {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return createImageBitmap(file);
  }
}

async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        cleanup: () => {
          if (typeof bitmap.close === 'function') bitmap.close();
        },
      };
    } catch {
      // Fall through to HTMLImageElement.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not decode image'));
      el.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      cleanup: () => URL.revokeObjectURL(url),
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

/**
 * Resize and re-encode an image File. Returns the original when the file is a
 * GIF or SVG, already under 300 KB, or when the encoded result is larger.
 * Never upscales.
 *
 * @param {File} file
 * @param {{ maxWidth: number, format: 'jpeg' | 'png' }} options
 * @returns {Promise<File>}
 */
export async function compressImage(file, { maxWidth, format } = {}) {
  if (!file || isGifOrSvg(file) || file.size < SKIP_UNDER_BYTES) return file;
  if (!maxWidth || maxWidth <= 0) return file;

  const output = outputSpec(format);
  let decoded;
  try {
    decoded = await decodeImage(file);
    const { source, width, height } = decoded;
    if (!width || !height) return file;

    const scale = width > maxWidth ? maxWidth / width : 1;
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    if (output.mime === 'image/jpeg') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, targetWidth, targetHeight);
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, targetWidth, targetHeight);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((result) => resolve(result), output.mime, output.quality);
    });
    if (!blob || blob.size > file.size) return file;

    return new File([blob], replaceExtension(file.name, output.ext), {
      type: output.mime,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  } finally {
    decoded?.cleanup?.();
  }
}
